import {uniqBy} from 'lodash';

import {List as ImmutableList, OrderedMap as ImmutableOrderedMap} from 'immutable';

import Facet from './Facet';

/* Utils */
import {preprocessRawAppliedPriceFilter} from '../../../modules/filter/filterUtils';

/* Constants */
import {FACET_IDS, FACET_QUERY_PARAM_NAMES} from './facetConstants';
import {DELIVERY} from '../../../modules/delivery/deliveryConstants';
import {
  EXPRESS_DELIVERY_FACET_CUSTOM_ID,
  EXPRESS_DELIVERY_FILTER_CUSTOM_ID,
  NEXT_DAY_DELIVERY_FACET_CUSTOM_ID,
  NEXT_DAY_DELIVERY_FILTER_CUSTOM_ID
} from '../../../modules/filter/filterConstants';

function generateFacetMap(rawFacets) {
  const facetMapEntries = [];

  rawFacets.forEach(
    rawFacet => {
      const {id: facetId} = rawFacet;

      facetMapEntries.push([
        facetId,
        new Facet(
          rawFacet
        )
      ]
      );
    }
  );

  return ImmutableOrderedMap(facetMapEntries);
}

function mergeFacetItems(existingItems, newItems, expandAmount) {
  return uniqBy(
    existingItems.concat(
      newItems.slice(
        newItems.size,
        expandAmount
      )
    ),
    filter => filter.id
  );
}

function expandFacetVisibleFilterListBy(facet, expandAmount, searchKey) {
  const facetItemsAfterSearch = mapFacetItemsByKeyword(facet.rawFacet.items, searchKey);
  const visibleFilterList = ImmutableList(
    mergeFacetItems(
      facet.visibleFilterList.toJS(),
      facetItemsAfterSearch,
      facet.visibleFilterList.size + expandAmount
    )
  );

  return {
    ...facet,
    visibleFilterList
  };
}

function replaceTurkishCharacter(keyword) {
  let replaceKeyword = keyword;

  replaceKeyword = keyword.replace(/ı/gi, 'i');
  replaceKeyword = replaceKeyword.replace(/ö/gi, 'o');
  replaceKeyword = replaceKeyword.replace(/ü/gi, 'u');
  replaceKeyword = replaceKeyword.replace(/ç/gi, 'c');
  replaceKeyword = replaceKeyword.replace(/ğ/gi, 'g');
  replaceKeyword = replaceKeyword.replace(/ş/gi, 's');

  return replaceKeyword;
}

function mapFacetItemsByKeyword(facetItems, searchKey) {
  let resultItems = [];

  if (searchKey) {
    facetItems.forEach(item => {
      const itemName = replaceTurkishCharacter(item.name.toLocaleLowerCase());
      const lowerSearchKey = replaceTurkishCharacter(searchKey.toLocaleLowerCase());

      if (itemName.indexOf(lowerSearchKey) === 0 || itemName.indexOf(' ' + lowerSearchKey) > -1) {
        resultItems.push(item);
      }
    });

    return resultItems;
  }

  return facetItems;
}

function generateAppliedFacetArray(rawAppliedFacets, deliveryInfo = null) {
  const appliedFacets = [...rawAppliedFacets];
  const handledDeliveryInfo = generateDeliveryAppliedFacet(deliveryInfo);

  const nextDayDeliveryInfo = handledDeliveryInfo?.find(item => item.customFacetId === NEXT_DAY_DELIVERY_FACET_CUSTOM_ID);
  const expressDeliveryInfo = handledDeliveryInfo?.find(item => item.customFacetId === EXPRESS_DELIVERY_FACET_CUSTOM_ID);

  const isAppliedNextDayDeliveryFilter = Boolean(nextDayDeliveryInfo?.items?.length);
  const isAppliedExpressDeliveryFilter = Boolean(expressDeliveryInfo?.items?.length);

  if (isAppliedNextDayDeliveryFilter) {
    appliedFacets.push(nextDayDeliveryInfo);
  }

  if (isAppliedExpressDeliveryFilter) {
    appliedFacets.push(expressDeliveryInfo);
  }

  return appliedFacets.map(
    rawAppliedFacet => {
      let result;

      if (rawAppliedFacet.id === FACET_IDS.PRICE) {
        result = Facet.fromRawAppliedFacet(
          preprocessRawAppliedPriceFacet(rawAppliedFacet)
        );
      } else {
        result = Facet.fromRawAppliedFacet(rawAppliedFacet);
      }

      return result;
    }
  );
}

function preprocessRawAppliedPriceFacet(rawPriceFacet) {
  return {
    ...rawPriceFacet,
    items: rawPriceFacet.items.map(
      preprocessRawAppliedPriceFilter
    )
  };
}

function generateAutoAppliedCount(rawAppliedFacets) {
  let count = 0;
  rawAppliedFacets.map(
    // eslint-disable-next-line array-callback-return
    rawAppliedFacet => {
      rawAppliedFacet.items.map(
        // eslint-disable-next-line array-callback-return
        item => {
          if (item.isAutoApplied) {
            count++;
          }
        }
      );
    }
  );
  return count;
}

function generateDeliveryAppliedFacet(deliveryInfo) {
  let delivery = [];
  if (deliveryInfo) {
    let nextDayDeliveryItems = [];
    let expressDeliveryItems = [];
    if (deliveryInfo?.nextDayDelivery?.selectedValues) {
      // eslint-disable-next-line no-unused-expressions,array-callback-return
      deliveryInfo?.nextDayDelivery?.selectedValues?.map((selectedValue) => {
        nextDayDeliveryItems.push({
          id: selectedValue.value,
          name: selectedValue.displayName,
          value: selectedValue.value,
          customFilterId: NEXT_DAY_DELIVERY_FILTER_CUSTOM_ID,
          isAutoApplied: false
        });
      });
      delivery.push({
        id: deliveryInfo?.nextDayDelivery?.key,
        title: deliveryInfo?.nextDayDelivery?.name,
        isIndexable: false,
        isIndexableWithBrandFilter: false,
        mordorId: deliveryInfo?.nextDayDelivery?.key,
        customFacetId: NEXT_DAY_DELIVERY_FACET_CUSTOM_ID,
        items: nextDayDeliveryItems
      });
    }

    if (deliveryInfo?.expressDelivery?.selectedValues) {
      // eslint-disable-next-line no-unused-expressions,array-callback-return
      deliveryInfo?.expressDelivery?.selectedValues?.map((selectedValue) => {
        expressDeliveryItems.push({
          id: selectedValue.value,
          name: selectedValue.displayName,
          value: selectedValue.value,
          customFilterId: EXPRESS_DELIVERY_FILTER_CUSTOM_ID,
          isAutoApplied: false
        });
      });
      delivery.push({
        id: deliveryInfo?.expressDelivery?.key,
        title: deliveryInfo?.expressDelivery?.name,
        isIndexable: false,
        isIndexableWithBrandFilter: false,
        mordorId: deliveryInfo?.nextDayDelivery?.key,
        customFacetId: EXPRESS_DELIVERY_FACET_CUSTOM_ID,
        items: expressDeliveryItems
      });
    }

    return delivery;
  }
  return null;
}

function deliveryRedirectionIfNecessary(queryParams, location, deliveryData, context, fetchOperationContext, callback) {
  let genericFiltersRegex;
  let nextDayDeliveryFilter;
  let expressDeliveryFilter;
  let parsedNextDayDeliveryFilterValue;
  let parsedExpressDeliveryFilterValue;
  if (queryParams && queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS]) {
    genericFiltersRegex = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].split(';');
    nextDayDeliveryFilter = genericFiltersRegex.find(filter => filter.includes(DELIVERY.NEXT_DAY_DELIVERY));
    expressDeliveryFilter = genericFiltersRegex.find(filter => filter.includes(DELIVERY.EXPRESS_DELIVERY));
    if (location?.city?.code && location?.city?.code !== 'null') {

      parsedNextDayDeliveryFilterValue = nextDayDeliveryFilter != null && nextDayDeliveryFilter.length > 1 ? nextDayDeliveryFilter.split(':')[1] : '';
      if (parsedNextDayDeliveryFilterValue && parsedNextDayDeliveryFilterValue !== location?.city?.code) {
        queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].replace(nextDayDeliveryFilter, `${DELIVERY.NEXT_DAY_DELIVERY}:${location?.city?.code}`);
        return callback(context, fetchOperationContext, queryParams);
      }

      parsedExpressDeliveryFilterValue = expressDeliveryFilter != null && expressDeliveryFilter.length > 1 ? expressDeliveryFilter.split(':')[1] : '';
      if (parsedExpressDeliveryFilterValue && parsedExpressDeliveryFilterValue !== deliveryData?.expressDelivery?.values?.map(value => value.value).
        join(',') && deliveryData?.expressDelivery?.isRedirect) {
        queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].replace(expressDeliveryFilter, `${DELIVERY.EXPRESS_DELIVERY}:${deliveryData?.expressDelivery?.values?.map(value => value.value).
          join(',')}`);
        return callback(context, fetchOperationContext, queryParams);
      }

    } else {
      if (nextDayDeliveryFilter) {
        const params = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].replace(nextDayDeliveryFilter, '');
        queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = params?.length > 0 ? params : null;
        return callback(context, fetchOperationContext, queryParams);
      }

      if (expressDeliveryFilter) {
        const params = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].replace(expressDeliveryFilter, '');
        queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = params?.length > 0 ? params : null;
        return callback(context, fetchOperationContext, queryParams);
      }
    }
  }

  return false;
}

function deliveryQueryParams(queryParams, location) {
  let parsedNextDayDeliveryFilterValue;
  let genericFiltersRegex;
  let nextDayDeliveryFilter;

  if (queryParams && queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS]) {
    genericFiltersRegex = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].split(';');
    nextDayDeliveryFilter = genericFiltersRegex.find(filter => filter.includes(DELIVERY.NEXT_DAY_DELIVERY));
    if (location?.city?.code && location?.city?.code !== 'null') {
      parsedNextDayDeliveryFilterValue = nextDayDeliveryFilter != null && nextDayDeliveryFilter.length > 1 ? nextDayDeliveryFilter.split(':')[1] : '';
      if (parsedNextDayDeliveryFilterValue && parsedNextDayDeliveryFilterValue !== location?.city?.code) {
        queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].replace(nextDayDeliveryFilter, `${DELIVERY.NEXT_DAY_DELIVERY}:${location?.city?.code}`);
        return queryParams;
      }
    } else {
      genericFiltersRegex = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].split(';');
      nextDayDeliveryFilter = genericFiltersRegex.find(filter => filter.includes(DELIVERY.NEXT_DAY_DELIVERY));
      if (nextDayDeliveryFilter) {
        let params = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].replace(nextDayDeliveryFilter, '');
        queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = params?.length > 0 ? params : null;
        return queryParams;
      }
    }
  }
  return false;
}

function deliveryValueIsNotEqualCityCode(queryParams, location) {
  let genericFiltersRegex;
  let nextDayDeliveryFilter;
  let parsedNextDayDeliveryFilterValue;

  if (queryParams && queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS]) {
    nextDayDeliveryFilter = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].indexOf(DELIVERY.NEXT_DAY_DELIVERY) > -1;
    if (location?.city?.code && location?.city?.code !== 'null') {
      if (nextDayDeliveryFilter) {
        genericFiltersRegex = queryParams[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS].split(';');
        nextDayDeliveryFilter = genericFiltersRegex.find(filter => filter.includes(DELIVERY.NEXT_DAY_DELIVERY));
        parsedNextDayDeliveryFilterValue = nextDayDeliveryFilter != null && nextDayDeliveryFilter.length > 1 ? nextDayDeliveryFilter.split(':')[1] : '';
        if (parsedNextDayDeliveryFilterValue && parsedNextDayDeliveryFilterValue !== location?.city?.code) {
          return true;
        }
      }
    }
    return false;
  }
  return false;
}

const isToggleFilter = facet => facet?.filterDisplayType === 'Toggle';

export {
  generateFacetMap,
  expandFacetVisibleFilterListBy,
  generateAppliedFacetArray,
  mapFacetItemsByKeyword,
  mergeFacetItems,
  generateAutoAppliedCount,
  generateDeliveryAppliedFacet,
  deliveryRedirectionIfNecessary,
  deliveryQueryParams,
  deliveryValueIsNotEqualCityCode,
  isToggleFilter
};
